/**
 * @file AssetManifest.ts
 * @role Maps bundle names to {assetId -> url}. Real art is added bundle-by-bundle at
 *       the milestone that needs it (placeholder art, but REAL asset IDs from day one).
 *       Components request assets by ID; URLs never leak out of AssetService.
 * @owns The manifest data shape + the (initially empty) v1 manifest.
 * @reads Nothing.
 * @neverDoes Load anything. AssetService owns loading.
 * @invariants 15 (asset access only via AssetService).
 * @see docs/CONFIG_GUIDE.md, docs/TASK_RECIPES.md "Add a new symbol"
 */
import type { AssetBundleName } from './AssetBundleName';
import neonFruitAtlasUrl from '@assets/themes/lucky-lights/backgrounds/neon-fruit-atlas.webp.json?url&no-inline';
import neonFruitAtlasImageUrl from '@assets/themes/lucky-lights/backgrounds/neon-fruit-atlas.webp?no-inline';
import reelFlipBigWinPlaceholder from '@assets/themes/lucky-lights/reels/reel-flip-big-win-placeholder.webp';
import reelFlipFreeSpins from '@assets/themes/lucky-lights/reels/reel-flip-free-spins.webp';
import buttonCoins from '@assets/themes/lucky-lights/ui/button-coins.webp?no-inline';
import buttonForward from '@assets/themes/lucky-lights/ui/button-forward.webp?no-inline';
import buttonMenu from '@assets/themes/lucky-lights/ui/button-menu.webp?no-inline';
import buttonPlay from '@assets/themes/lucky-lights/ui/button-play.webp?no-inline';
import buttonReplay from '@assets/themes/lucky-lights/ui/button-replay.webp?no-inline';
import baseSymbolAtlasUrl from '@assets/symbols/base-symbols.webp.json?url&no-inline';
import baseSymbolAtlasImageUrl from '@assets/symbols/base-symbols.webp?no-inline';
import motionSymbolAtlasUrl from '@assets/symbols/motion-symbols.webp.json?url&no-inline';
import motionSymbolAtlasImageUrl from '@assets/symbols/motion-symbols.webp?no-inline';
import symbolStrawberry from '@assets/symbols/symbol-strawberry.webp';
import symbolPear from '@assets/symbols/symbol-pear.webp';
import iconClose from '@assets/ui/icon-close.svg';
import iconInfo from '@assets/ui/icon-info.svg';
import iconMinus from '@assets/ui/icon-minus.svg';
import iconPlus from '@assets/ui/icon-plus.svg';
import titleFontBungee from '@assets/themes/lucky-lights/fonts/bungee-latin.woff2?url&no-inline';
import titleFontLilitaOne from '@assets/themes/lucky-lights/fonts/lilita-one-latin.woff2?url&no-inline';
import titleFontRighteous from '@assets/themes/lucky-lights/fonts/righteous-latin.woff2?url&no-inline';
import backgroundMidnightLandscape from '@assets/themes/midnight-fruit-club/backgrounds/main-landscape.webp?no-inline';
import backgroundMidnightPortrait from '@assets/themes/midnight-fruit-club/backgrounds/main-portrait.webp?no-inline';
import reelFrameMidnight1x from '@assets/themes/midnight-fruit-club/reels/reel-frame-v2@1x.webp?no-inline';
import reelFrameMidnight2x from '@assets/themes/midnight-fruit-club/reels/reel-frame-v2@2x.webp?no-inline';
import reelCellMidnight from '@assets/themes/midnight-fruit-club/reels/reel-cell.svg?no-inline';
import backgroundInfoMidnight from '@assets/themes/midnight-fruit-club/ui/background-info.webp?no-inline';
import backgroundScoreMidnight from '@assets/themes/midnight-fruit-club/ui/background-score.webp?no-inline';
import buttonRoundMidnight from '@assets/themes/midnight-fruit-club/ui/button-round-background.webp?no-inline';
import buttonSpinMidnight from '@assets/themes/midnight-fruit-club/ui/button-spin.webp?no-inline';
import controlDockMidnight from '@assets/themes/midnight-fruit-club/ui/control-dock.webp?no-inline';
import portraitActionDockMidnight from '@assets/themes/midnight-fruit-club/ui/portrait-action-dock.svg?no-inline';
import portraitStatusPanelMidnight from '@assets/themes/midnight-fruit-club/ui/portrait-status-panel.svg?no-inline';
import portraitInfoButtonMidnight from '@assets/themes/midnight-fruit-club/ui/portrait-info-button.svg?no-inline';
import landscapeStatusRailMidnight from '@assets/themes/midnight-fruit-club/ui/landscape-status-rail.svg?no-inline';
import desktopControlFrameMidnight from '@assets/themes/midnight-fruit-club/ui/desktop-control-frame.svg?no-inline';
import desktopInfoButtonMidnight from '@assets/themes/midnight-fruit-club/ui/desktop-info-button.svg?no-inline';
import desktopSpinButtonMidnight from '@assets/themes/midnight-fruit-club/ui/desktop-spin-button.svg?no-inline';
import desktopSpinIconMidnight from '@assets/themes/midnight-fruit-club/ui/desktop-spin-icon.svg?no-inline';
import titleFontMidnightBebas from '@assets/themes/midnight-fruit-club/fonts/bebas-neue-regular.woff2?url&no-inline';

export interface BundleAssetDescriptor {
  alias?: string | string[];
  data?: Record<string, unknown>;
  src: string | string[];
}

export type BundleAssets = Record<string, string | BundleAssetDescriptor>;
export type AssetManifest = Partial<Record<AssetBundleName, BundleAssets>>;

const BASE_SYMBOL_TEXTURE_IDS = [
  'symbol_A',
  'symbol_K',
  'symbol_Q',
  'symbol_WILD',
  'symbol_SCATTER',
  'symbol_ORANGE',
  'symbol_LEMON',
  'symbol_SEVEN',
  'symbol_BELL',
  'symbol_STAR',
  'symbol_APPLE',
  'symbol_BANANA',
];
const MOTION_SYMBOL_TEXTURE_IDS = BASE_SYMBOL_TEXTURE_IDS.map((id) => `${id}_motion`);
const NEON_FRUIT_TEXTURE_IDS = [
  'background_neon_cherry',
  'background_neon_lemon',
  'background_neon_orange',
  'background_neon_watermelon',
  'background_neon_plum',
  'background_neon_grape',
];

const assetFilename = (url: string): string => url.slice(url.lastIndexOf('/') + 1);

/**
 * v1 manifest. Bundles are populated as their milestone arrives (e.g. `reels` at M5).
 * Empty bundles are tolerated by AssetService so boot never 404s on missing art.
 */
export const ASSET_MANIFEST: AssetManifest = {
  splash: {
    title_font_midnight_bebas: {
      src: titleFontMidnightBebas,
      data: { family: 'Midnight Bebas Neue', weights: ['400'], display: 'block' },
    },
    title_font_righteous: {
      src: titleFontRighteous,
      data: { family: 'Lucky Righteous', weights: ['400'], display: 'block' },
    },
  },
  core: {
    background_neon_fruit_atlas: {
      alias: ['background_neon_fruit_atlas', ...NEON_FRUIT_TEXTURE_IDS],
      src: neonFruitAtlasUrl,
      data: { imageFilename: assetFilename(neonFruitAtlasImageUrl) },
    },
  },
  background_landscape: {
    background_midnight_landscape: backgroundMidnightLandscape,
  },
  background_portrait: {
    background_midnight_portrait: backgroundMidnightPortrait,
  },
  reels: {
    ui_midnight_reel_frame: {
      src: [reelFrameMidnight1x, reelFrameMidnight2x],
      data: { scaleMode: 'linear' },
    },
    ui_midnight_reel_cell: {
      src: reelCellMidnight,
      data: { resolution: 2, scaleMode: 'linear' },
    },
    symbol_STRAWBERRY: symbolStrawberry,
    symbol_PEAR: symbolPear,
    // Pixi caches each spritesheet frame under its existing symbol ID. The atlas descriptor also
    // exposes those aliases to boot-time skin validation without changing any consumers.
    base_symbol_atlas: {
      alias: ['base_symbol_atlas', ...BASE_SYMBOL_TEXTURE_IDS],
      src: baseSymbolAtlasUrl,
      data: { imageFilename: assetFilename(baseSymbolAtlasImageUrl) },
    },
  },
  free_spins: {
    ui_reel_flip_free_spins: reelFlipFreeSpins,
  },
  preview: {
    ui_reel_flip_big_win_placeholder: reelFlipBigWinPlaceholder,
  },
  reel_motion: {
    motion_symbol_atlas: {
      alias: ['motion_symbol_atlas', ...MOTION_SYMBOL_TEXTURE_IDS],
      src: motionSymbolAtlasUrl,
      data: { imageFilename: assetFilename(motionSymbolAtlasImageUrl) },
    },
  },
  ui: {
    title_font_bungee: {
      src: titleFontBungee,
      data: { family: 'Lucky Bungee', weights: ['400'], display: 'block' },
    },
    title_font_lilita_one: {
      src: titleFontLilitaOne,
      data: { family: 'Lucky Lilita One', weights: ['400'], display: 'block' },
    },
    icon_autoplay: buttonForward,
    icon_info: iconInfo,
    icon_close: iconClose,
    icon_coin_options: buttonCoins,
    icon_menu: buttonMenu,
    icon_minus: iconMinus,
    icon_play: buttonPlay,
    icon_plus: iconPlus,
    icon_spin: buttonReplay,
    ui_midnight_background_info: backgroundInfoMidnight,
    ui_midnight_background_score: backgroundScoreMidnight,
    ui_midnight_button_round_background: {
      alias: ['ui_midnight_button_round_background', 'ui_button_round_background'],
      src: buttonRoundMidnight,
    },
    ui_midnight_button_spin: buttonSpinMidnight,
    ui_midnight_control_dock: controlDockMidnight,
    ui_midnight_portrait_action_dock: portraitActionDockMidnight,
    ui_midnight_portrait_status_panel: portraitStatusPanelMidnight,
    ui_midnight_portrait_info_button: portraitInfoButtonMidnight,
    ui_midnight_landscape_status_rail: landscapeStatusRailMidnight,
    ui_midnight_desktop_control_frame: desktopControlFrameMidnight,
    ui_midnight_desktop_info_button: desktopInfoButtonMidnight,
    ui_midnight_desktop_spin_button: desktopSpinButtonMidnight,
    ui_midnight_desktop_spin_icon: desktopSpinIconMidnight,
  },
};

/** Flatten the manifest for boot-time skin validation; does not load any assets. */
export function assetManifestIds(manifest: AssetManifest = ASSET_MANIFEST): ReadonlySet<string> {
  return new Set(
    Object.values(manifest).flatMap((bundle) =>
      Object.entries(bundle ?? {}).flatMap(([assetId, asset]) => {
        if (typeof asset === 'string' || !asset.alias) return [assetId];
        return [assetId, ...(typeof asset.alias === 'string' ? [asset.alias] : asset.alias)];
      }),
    ),
  );
}

/** Stable manifest-derived asset IDs owned by the active Midnight Fruit Club presentation. */
export const MIDNIGHT_FRUIT_CLUB_ASSET_IDS: readonly string[] = [...assetManifestIds()].filter(
  (assetId) => assetId.includes('_midnight_'),
);

/** Textures worth uploading while the splash still hides GPU preparation work. */
export const CRITICAL_GPU_TEXTURE_IDS: readonly string[] = [
  // Exactly one orientation background is present during boot; missing optional ids are filtered.
  'background_midnight_landscape',
  'background_midnight_portrait',
  // One frame uploads the shared neon-decoration atlas source.
  'background_neon_cherry',
  'ui_midnight_reel_frame',
  'ui_midnight_reel_cell',
  // One frame uploads the shared base-atlas source used by every sharp symbol.
  'symbol_A',
  ...Object.keys(ASSET_MANIFEST.ui ?? {}).filter((id) => !id.startsWith('title_font_')),
];
